/**
 * "The session just died and nothing can recover it" — broadcast from the api client, consumed by
 * `useAuth`.
 *
 * `services/api/client.ts` already detected this correctly: on a 401 whose refresh came back
 * definitively rejected, it drops `gisify_token` and `gisify_user`. What it could not do is tell
 * React. `useAuth` holds the session in a `user` state object, `isAuthenticated` is `!!user`, and
 * `AuthGuard` runs its `checkAuth()` once on mount — so the session was dead in localStorage and
 * alive in memory, and the guard kept rendering the app. The user got an error card on a
 * live-looking page ("Failed to load dashboards: Authorization failed: ..."), not the login screen,
 * and only a manual reload — which re-reads the now-empty localStorage — got them out. Observed on
 * the gisify.app on-prem install 2026-10-16, where Keycloak's 30-minute SSO idle timeout makes a
 * definitively-dead session an ordinary daily event rather than a rarity.
 *
 * React-free and dependency-free on purpose, exactly like `insufficient-credits-emitter.ts`: the
 * api client is imported by non-React code, and an import edge from it into the hook would be a
 * cycle.
 */

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Called by the api client when it tears down a session that the identity provider permanently
 * rejected. NOT called for a transient failure — those leave the refresh token in place precisely
 * so the session can still recover, and ending it there is the bug that guard exists to prevent.
 */
export function emitSessionExpired(): void {
  listeners.forEach((l) => l())
}

/** Subscribe (the `AuthProvider`). Returns an unsubscribe fn. */
export function subscribeSessionExpired(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}
